import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import SearchBar from '../../components/searchBar/SearchBar';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import BlogCategoryCard from '../../components/blog/BlogCategoryCard';
import Pagination from '../../components/tables/pagination/Pagination';
import { confirmDanger } from '../../utils/confirmDialog';
import { getBlogCategories, deleteBlogCategory, patchBlogCategory } from '../../services/blogCategory';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';

const BlogCategories = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0, next: null, previous: null });
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);

    const fetchCategories = async (query = '') => {
        setLoading(true);
        try {
            const response = await getBlogCategories(query);
            if (response.status === 200) {
                setData(response.data);
            } else {
                toast.error('Failed to fetch categories');
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (query) => {
        setSearchText(query);
        const searchQuery = query ? `?search=${query}` : '';
        fetchCategories(searchQuery);
    };

    const handleCreateCategory = () => {
        navigate('/blog/categories/create');
    };

    const handleEditCategory = (category) => {
        navigate(`/blog/categories/edit/${category.id}`);
    };

    const handleDeleteCategory = async (category) => {
        const confirmation = await confirmDanger(
            `Are you sure you want to delete "${category.name}"? This action cannot be undone.`,
            "Delete Category"
        );

        if (confirmation) {
            try {
                const response = await deleteBlogCategory(category.id);
                if (response.status === 204) {
                    toast.success('Category deleted successfully');
                    fetchCategories(searchText ? `?search=${searchText}` : '');
                } else {
                    toast.error('Failed to delete category');
                }
            } catch (error) {
                console.error('Error deleting category:', error);
                toast.error('Failed to delete category');
            }
        }
    };

    const handleToggleStatus = async (category) => {
        try {
            const response = await patchBlogCategory({
                id: category.id,
                data: { is_active: !category.is_active }
            });
            if (response.status === 200) {
                toast.success(`Category ${!category.is_active ? 'activated' : 'deactivated'} successfully`);
                fetchCategories(searchText ? `?search=${searchText}` : '');
            } else {
                toast.error('Failed to update category status');
            }
        } catch (error) {
            console.error('Error updating category status:', error);
            toast.error('Failed to update category status');
        }
    };


    useEffect(() => {
        fetchCategories();
    }, []);

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Blog', link: '/blog/dashboard' },
                { name: 'Categories' },
            ]} />

            <div className="py-3">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                    <StyledHeadingText text="Blog Categories" />
                    <Button
                        onClick={handleCreateCategory}
                        className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
                    >
                        <FaPlus size={16} />
                        <span>Add Category</span>
                    </Button>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <SearchBar
                        placeholder="Search categories..."
                        onSearch={handleSearch}
                        value={searchText}
                    />
                </div>

                {/* Categories Grid */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <CustomLoading size={100} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {data.results.map((category) => (
                            <BlogCategoryCard
                                key={category.id}
                                category={category}
                                onEdit={handleEditCategory}
                                onDelete={handleDeleteCategory}
                                onToggleStatus={handleToggleStatus}
                            />
                        ))}
                    </div>
                )}

                {!loading && data.results.length === 0 && (
                    <div className="text-center py-12">
                        <div className="text-gray-500 text-lg mb-4">No categories found</div>
                        <Button
                            onClick={handleCreateCategory}
                            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary/90 transition-colors"
                        >
                            Create First Category
                        </Button>
                    </div>
                )}

                {/* Pagination */}
                {data.count > 0 && (
                    <div className="mt-8">
                        <Pagination
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalEntries={data.count}
                            onPageChange={setCurrentPage}
                            data={data}
                            setData={setData}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default BlogCategories;
